/**
 * The shop's own colour.
 *
 * The owner picks ONE colour. Everything else - the hover shade, the pale
 * background behind a highlighted row, the darker shade for text on it, and the
 * colour of the text sitting ON the accent - is worked out from that one choice,
 * separately for the light and dark interface.
 *
 * The reason this is arithmetic rather than a second colour picker: a shop owner
 * choosing five colours by hand will sooner or later choose white text on yellow,
 * and the till becomes unreadable at the one moment it matters. Here the text on
 * a button is whichever of black or white the eye can actually read, measured,
 * not guessed.
 */

export type ThemeMode = 'light' | 'dark';

export interface AccentShades {
  /** The colour itself, adjusted if it would be invisible on this background. */
  accent: string;
  /** Pressed and hovered. */
  hover: string;
  /** The pale wash behind a selected row or a soft badge. */
  soft: string;
  /** Text sitting ON the soft wash. */
  ink: string;
  /** Text sitting ON the accent - black or white, whichever can be read. */
  onAccent: string;
}

// --- plumbing ----------------------------------------------------------------

export function parseHex(input: string): [number, number, number] | null {
  const s = String(input ?? '').trim().replace(/^#/, '');
  const full = s.length === 3 ? s.split('').map((c) => c + c).join('') : s;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

const clamp = (n: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, n));
const hex2 = (n: number) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, '0');
const toHex = (r: number, g: number, b: number) => `#${hex2(r)}${hex2(g)}${hex2(b)}`;

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === R) h = ((G - B) / d + (G < B ? 6 : 0)) / 6;
  else if (max === G) h = ((B - R) / d + 2) / 6;
  else h = ((R - G) / d + 4) / 6;
  return [h, s, l];
}

export function hslToHex(h: number, s: number, l: number): string {
  const H = ((h % 1) + 1) % 1, S = clamp(s), L = clamp(l);
  if (S === 0) return toHex(L * 255, L * 255, L * 255);
  const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
  const p = 2 * L - q;
  const ch = (t: number) => {
    let T = t;
    if (T < 0) T += 1;
    if (T > 1) T -= 1;
    if (T < 1 / 6) return p + (q - p) * 6 * T;
    if (T < 1 / 2) return q;
    if (T < 2 / 3) return p + (q - p) * (2 / 3 - T) * 6;
    return p;
  };
  return toHex(ch(H + 1 / 3) * 255, ch(H) * 255, ch(H - 1 / 3) * 255);
}

/** How bright a colour is to the eye, the way the accessibility standard measures it. */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex) ?? [0, 0, 0];
  const [R, G, B] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/** 1 = identical, 21 = black on white. Body text wants 4.5 or more. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white - whichever can actually be read on this background. */
export function readableOn(background: string): string {
  const onWhite = contrastRatio(background, '#ffffff');
  const onBlack = contrastRatio(background, '#111111');
  return onWhite >= onBlack ? '#ffffff' : '#111111';
}

// --- the one function the app calls ------------------------------------------

export const DEFAULT_ACCENT = '#b4542e';

/**
 * A very dark accent disappears on a dark interface, and a very pale one
 * disappears on a light one. Both are nudged into a band where they still read
 * as the colour the owner chose.
 */
export function accentShades(input: string, mode: ThemeMode): AccentShades {
  const rgb = parseHex(input) ?? parseHex(DEFAULT_ACCENT)!;
  const [h, rawS, rawL] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  // A colour with almost no saturation cannot carry meaning; give it a floor.
  const s = Math.max(rawS, 0.18);

  if (mode === 'dark') {
    const l = clamp(rawL, 0.45, 0.72);           // must glow against near-black
    const accent = hslToHex(h, s, l);
    return {
      accent,
      hover: hslToHex(h, s, clamp(l + 0.08, 0, 0.86)),
      soft: hslToHex(h, Math.min(s, 0.5), 0.16),  // a dark tint, not a pale wash
      ink: hslToHex(h, Math.min(s, 0.7), 0.78),
      onAccent: readableOn(accent),
    };
  }

  const l = clamp(rawL, 0.24, 0.56);             // must hold its own on paper
  const accent = hslToHex(h, s, l);
  return {
    accent,
    hover: hslToHex(h, s, clamp(l - 0.08, 0.1, 1)),
    soft: hslToHex(h, Math.min(s, 0.6), 0.95),
    ink: hslToHex(h, s, clamp(l - 0.14, 0.14, 1)),
    onAccent: readableOn(accent),
  };
}

/** The colours offered as swatches, so most shops never type a hex code. */
export const ACCENT_PRESETS: { key: string; hex: string; en: string; ar: string }[] = [
  { key: 'clay', hex: '#b4542e', en: 'Clay', ar: 'طيني' },
  { key: 'olive', hex: '#5f7138', en: 'Olive', ar: 'زيتي' },
  { key: 'teal', hex: '#1f6f6b', en: 'Teal', ar: 'أزرق مخضر' },
  { key: 'navy', hex: '#2d4a7c', en: 'Navy', ar: 'كحلي' },
  { key: 'plum', hex: '#6d3a63', en: 'Plum', ar: 'خوخي' },
  { key: 'rose', hex: '#a83a55', en: 'Rose', ar: 'وردي' },
  { key: 'amber', hex: '#9a6516', en: 'Amber', ar: 'كهرماني' },
  { key: 'slate', hex: '#4a5560', en: 'Slate', ar: 'رمادي' },
];
